import onetime from "@-/com.npmjs.onetime";

import manifestData from "#slides-manifest" with { type: "json" };
import type { SlideManifestEntry } from "#slide-anchor";

/**
 * The checked-in slide manifest: every slide's key + slug paired with the anchor
 * that positions it, in stable path order.
 *
 * Generated by `bun run gen` in this package (see `-build-slides-manifest.ts`), which is
 * the only code that reads slide CONTENT. Consuming the generated JSON here keeps the
 * runtime ordering path free of MDX parsing, so it works identically under Metro and
 * under Node/bun seed, Vite, and Vitest — none of which can compile MDX.
 */
export const getSlideManifest: () => SlideManifestEntry[] = onetime(() => manifestData);

/** Every authored slide slug, in stable path order. Carries NO ordering meaning. */
export const getSlideSlugs: () => string[] = onetime(() =>
  getSlideManifest().map((entry) => entry.slug),
);

/** Every authored slide key (UUID), in stable path order. */
export const getSlideKeys: () => string[] = onetime(() =>
  getSlideManifest().map((entry) => entry.key),
);

const getSlideBySlug: () => Map<string, SlideManifestEntry> = onetime(
  () => new Map(getSlideManifest().map((entry) => [entry.slug, entry] as const)),
);

const getSlideByKey: () => Map<string, SlideManifestEntry> = onetime(
  () => new Map(getSlideManifest().map((entry) => [entry.key, entry] as const)),
);

/** Resolve a URL slug to its unchanging UUID key, or `undefined` if the slug is not in the catalogue. */
export function getSlideKeyBySlug(slug: string): string | undefined {
  return getSlideBySlug().get(slug)?.key;
}

/** Resolve a UUID key to its URL slug, or `undefined` if the key is not in the catalogue. */
export function getSlideSlugByKey(key: string): string | undefined {
  return getSlideByKey().get(key)?.slug;
}

/** Look up a manifest entry by URL slug. */
export function getSlideManifestEntryBySlug(slug: string): SlideManifestEntry | undefined {
  return getSlideBySlug().get(slug);
}

/** Look up a manifest entry by UUID key. */
export function getSlideManifestEntryByKey(key: string): SlideManifestEntry | undefined {
  return getSlideByKey().get(key);
}
